import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { KPICard, KPIGrid } from '../../components/admin/KPICards';
import { Gift, Award, CheckCircle, Clock, Mail, Phone, MessageCircle, Calendar, History, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate next 6 months for selection
const generateMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return months;
};

function RewardCard({ client, onApplyReward, onContact }) {
  const handleWhatsApp = () => {
    const phone = client.phone || '+34623243967';
    const message = encodeURIComponent(`Hola ${client.name}, te contactamos desde Manklar respecto a tu bonificación.`);
    window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const handleEmail = () => {
    window.open(`mailto:${client.email}?subject=Manklar - Tu bonificación está lista`);
  };

  const handleCall = () => {
    const phone = client.phone || '+34623243967';
    window.open(`tel:${phone.replace(/\s/g, '')}`);
  };

  return (
    <div 
      className="p-6 rounded-lg bg-gradient-to-br from-[#0F0F0F] to-[#0A0A0A] border border-white/20 shadow-[0_0_30px_rgba(255,255,255,0.05)]"
      data-testid={`reward-card-${client.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
            <span className="text-xl font-light text-white">
              {client.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white">{client.name}</h3>
            <p className="text-sm text-zinc-500">{client.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-3xl">🎁</span>
        </div>
      </div>

      <div className="flex items-center gap-6 mb-4 p-4 rounded-lg bg-white/5">
        <div className="text-center">
          <p className="text-3xl font-light text-white font-['Outfit']">
            {client.available_rewards}
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            {client.available_rewards === 1 ? 'Bonificación' : 'Bonificaciones'}
          </p>
        </div>
        <div className="flex-1 border-l border-white/10 pl-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-zinc-500">Código:</span>
            <span className="font-mono text-sm text-white">{client.referral_code}</span>
          </div>
          <p className="text-sm text-zinc-400">
            Equivale a {client.available_rewards} {client.available_rewards === 1 ? 'mes' : 'meses'} gratis
          </p>
        </div>
      </div>

      {/* Pending Months Display */}
      {client.available_rewards > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-amber-400 font-medium">Meses aplicables</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {generateMonthOptions().slice(0, client.available_rewards).map(month => (
              <span key={month.value} className="px-2 py-1 rounded bg-amber-500/20 text-amber-300 text-xs">
                {month.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Applied History */}
      {client.applied_rewards && client.applied_rewards.length > 0 && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-2">
            <History className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-emerald-400 font-medium">Historial de bonificaciones aplicadas</span>
          </div>
          <div className="space-y-1">
            {client.applied_rewards.map((reward) => (
              <div key={`${reward.month_label}-${reward.applied_date}`} className="flex items-center justify-between text-xs">
                <span className="text-emerald-300">{reward.month_label}</span>
                <span className="text-zinc-500">{reward.applied_date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleWhatsApp}
          className="flex-1 h-10 bg-[#25D366]/10 border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/20"
          data-testid={`contact-whatsapp-${client.id}`}
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          WhatsApp
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleEmail}
          className="h-10 bg-transparent border-white/20 text-zinc-300 hover:bg-white/10 hover:text-white"
          data-testid={`contact-email-${client.id}`}
        >
          <Mail className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCall}
          className="h-10 bg-transparent border-white/20 text-zinc-300 hover:bg-white/10 hover:text-white"
          data-testid={`contact-phone-${client.id}`}
        >
          <Phone className="w-4 h-4" />
        </Button>
        <Button
          size="sm"
          onClick={() => onApplyReward(client)}
          className="flex-1 h-10 bg-white text-black hover:bg-zinc-200"
          data-testid={`apply-reward-btn-${client.id}`}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Aplicar bonificación
        </Button>
      </div>
    </div>
  );
}

export default function AdminRewards() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [pendingRewards, setPendingRewards] = useState([]);
  const [appliedRewardsHistory, setAppliedRewardsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [applying, setApplying] = useState(false);

  const fetchPendingRewards = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/pending-rewards`, { 
        withCredentials: true 
      });
      setPendingRewards(response.data);
      
      // Also fetch applied rewards history
      try {
        const historyResponse = await axios.get(`${API_URL}/api/admin/applied-rewards`, { 
          withCredentials: true 
        });
        setAppliedRewardsHistory(historyResponse.data);
      } catch (historyErr) {
        // Endpoint might not exist yet, silently ignore
        console.debug('Applied rewards history not available:', historyErr.message);
      }
    } catch (err) {
      console.error('Error fetching pending rewards:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingRewards();
  }, [fetchPendingRewards]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleOpenApplyModal = (client) => {
    setSelectedClient(client);
    setSelectedMonth('');
    setShowApplyModal(true);
  };

  const handleApplyReward = async () => {
    if (!selectedClient || !selectedMonth) {
      toast.error('Selecciona un mes para aplicar la bonificación');
      return;
    }

    setApplying(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/apply-reward`,
        {
          client_id: selectedClient.id,
          month: selectedMonth
        },
        { withCredentials: true }
      );
      
      toast.success(`¡Bonificación aplicada para ${selectedClient.name}!`, {
        description: `Mes gratuito: ${generateMonthOptions().find(m => m.value === selectedMonth)?.label}`
      });
      
      setShowApplyModal(false);
      setSelectedClient(null);
      setSelectedMonth('');
      
      // Refresh data
      await fetchPendingRewards();
    } catch (err) {
      console.error('Error applying reward:', err);
      toast.error('Error al aplicar la bonificación', {
        description: err.response?.data?.detail || 'Inténtalo de nuevo'
      });
    } finally {
      setApplying(false);
    }
  };

  // Calculate total rewards
  const totalRewards = pendingRewards.reduce((acc, c) => acc + c.available_rewards, 0);

  if (loading) {
    return (
      <AdminLayout
        title="Recompensas"
        subtitle="Cargando..."
        onLogout={handleLogout}
      >
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Bonificaciones"
      subtitle={`${pendingRewards.length} clientes con bonificaciones pendientes`}
      pendingRewards={totalRewards}
      onLogout={handleLogout}
    >
      {/* Stats */}
      <KPIGrid className="mb-8">
        <KPICard
          title="Clientes con Bonificaciones"
          value={pendingRewards.length}
          icon={Gift}
          variant="accent"
        />
        <KPICard
          title="Total Bonificaciones Pendientes"
          value={totalRewards}
          subtitle={`${totalRewards} meses gratis por aplicar`}
          icon={Award}
          variant="highlight"
        />
        <KPICard
          title="Bonificaciones Aplicadas"
          value={appliedRewardsHistory.length}
          subtitle="Este mes"
          icon={CheckCircle}
          variant="success"
        />
      </KPIGrid>

      {/* Rewards List */}
      {pendingRewards.length === 0 ? (
        <div className="p-12 rounded-lg text-center bg-[#0A0A0A] border border-white/10">
          <Gift className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2 font-['Outfit']">
            No hay bonificaciones pendientes
          </h3>
          <p className="text-sm text-zinc-500 max-w-md mx-auto">
            Las bonificaciones aparecerán aquí cuando los referidos completen sus 3 pagos mensuales y pasen a estado VALIDADO
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="pending-rewards-list">
          {pendingRewards.map(client => (
            <RewardCard 
              key={client.id} 
              client={client} 
              onApplyReward={handleOpenApplyModal}
            />
          ))}
        </div>
      )}

      {/* Apply Reward Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-400" />
              Aplicar Bonificación
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <div className="py-4">
              <div className="flex items-center gap-4 mb-6 p-4 rounded-lg bg-white/5">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <span className="text-lg font-light text-white">
                    {selectedClient.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-white font-medium">{selectedClient.name}</p>
                  <p className="text-sm text-zinc-500">{selectedClient.email}</p>
                  <p className="text-xs text-amber-400 mt-1">
                    {selectedClient.available_rewards} bonificación{selectedClient.available_rewards !== 1 ? 'es' : ''} disponible{selectedClient.available_rewards !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">
                    Selecciona el mes a bonificar *
                  </label>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="w-full h-12 bg-[#121212] border-white/10 text-white">
                      <SelectValue placeholder="Selecciona un mes" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {generateMonthOptions().map(month => (
                        <SelectItem 
                          key={month.value} 
                          value={month.value}
                          className="text-zinc-300 focus:text-white focus:bg-white/10"
                        >
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs text-amber-400">
                    Al aplicar la bonificación, el cliente no pagará la cuota del mes seleccionado.
                    Esta acción quedará registrada en el historial.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowApplyModal(false)}
              className="text-zinc-400 hover:text-white hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleApplyReward}
              disabled={!selectedMonth || applying}
              className={`rounded-lg ${
                !selectedMonth 
                  ? 'bg-zinc-600 text-zinc-400 cursor-not-allowed' 
                  : 'bg-white text-black hover:bg-zinc-200'
              }`}
              data-testid="confirm-apply-reward-btn"
            >
              {applying ? 'Aplicando...' : selectedMonth ? 'Confirmar Bonificación' : 'Selecciona un mes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
